// `useWizard()` — top-level state machine for the 6-step onboarding wizard.
//
// Behaviour:
//   - On mount, fetches `GET /me/onboarding` and seeds the in-memory state
//     to the resumed step (US-038).
//   - Mutations (`patch`, `goto`) optimistically update the local snapshot
//     then fire `PATCH /me/onboarding` to persist.
//   - `complete()` fires `POST /me/onboarding/complete` and resolves with
//     the canonical user record so the caller can route to the goal page.
//
// We deliberately keep the local `state` independent of the server cache
// during step transitions: each click should feel instant, and a 200ms
// network blip shouldn't block the next-step animation.

import { useCallback, useEffect, useState } from 'react';

import {
  useCompleteOnboarding,
  useOnboardingState,
  useUpdateOnboarding,
  type OnboardingProgress,
} from '@barel/api-client/react';

import { initialProgress, mergeProgress, resumeStep } from './persistence';

export interface UseWizardOptions {
  /** Disable the network round-trip — tests pass `enabled: false`. */
  enabled?: boolean;
}

export interface UseWizardReturn {
  step: number;
  state: OnboardingProgress;
  isLoading: boolean;
  isCompleted: boolean;
  /** Resume server-side state into the local snapshot. */
  hydrate: (next: OnboardingProgress) => void;
  /** Apply a patch + persist to the API (debounced internally by react-query). */
  patch: (next: Partial<OnboardingProgress>) => void;
  /** Move to a specific step (clamped) + persist. */
  goto: (next: number) => void;
  /** Move forward one step + persist. */
  next: () => void;
  /** Move backward one step + persist. */
  back: () => void;
  /** Complete onboarding — clears wizard state, sets `goal` + `defaultInboxMode`. */
  complete: () => Promise<void>;
}

export function useWizard(options: UseWizardOptions = {}): UseWizardReturn {
  const enabled = options.enabled ?? true;
  const remote = useOnboardingState({ enabled });
  const update = useUpdateOnboarding();
  const completeMutation = useCompleteOnboarding();

  const [state, setState] = useState<OnboardingProgress>(initialProgress);
  const [hydratedRef, setHydratedRef] = useState(false);

  // Resume the wizard at the persisted step on first server hit.
  useEffect(() => {
    if (hydratedRef) return;
    if (!remote.data) return;
    const step = resumeStep(remote.data);
    const persisted = remote.data.progress;
    if (persisted) {
      setState({ ...persisted, step });
    } else {
      setState({ ...initialProgress(), step });
    }
    setHydratedRef(true);
  }, [hydratedRef, remote.data]);

  const persist = useCallback(
    (patch: Partial<OnboardingProgress>) => {
      if (!enabled) return;
      update.mutate(patch);
    },
    [enabled, update],
  );

  const patchFn = useCallback(
    (patch: Partial<OnboardingProgress>) => {
      setState((prev) => mergeProgress(prev, patch));
      persist(patch);
    },
    [persist],
  );

  const gotoFn = useCallback(
    (next: number) => {
      const clamped = Math.max(1, Math.min(6, Math.floor(next)));
      setState((prev) => mergeProgress(prev, { step: clamped }));
      persist({ step: clamped });
    },
    [persist],
  );

  const nextFn = useCallback(() => {
    setState((prev) => {
      const merged = mergeProgress(prev, { step: prev.step + 1 });
      persist({ step: merged.step });
      return merged;
    });
  }, [persist]);

  const backFn = useCallback(() => {
    setState((prev) => {
      const merged = mergeProgress(prev, { step: prev.step - 1 });
      persist({ step: merged.step });
      return merged;
    });
  }, [persist]);

  const completeFn = useCallback(async () => {
    await completeMutation.mutateAsync({
      goal: state.goal,
      inboxMode: state.inboxMode,
    });
  }, [completeMutation, state.goal, state.inboxMode]);

  const hydrateFn = useCallback((next: OnboardingProgress) => {
    setState(next);
    setHydratedRef(true);
  }, []);

  return {
    step: state.step,
    state,
    isLoading: enabled ? !hydratedRef && remote.isLoading : false,
    isCompleted: !!remote.data?.completedAt,
    hydrate: hydrateFn,
    patch: patchFn,
    goto: gotoFn,
    next: nextFn,
    back: backFn,
    complete: completeFn,
  };
}
